import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box';
import { ACard } from "@atiautomacao/ati-ui-library";
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TicketAttachments from './TicketAttachments';
import TicketMessages from './TicketMessages';

import {Ticket} from "../../../../Shared/Types/Ticket";
import {TicketCategory} from "../../../../Shared/Types/TicketCategory";
import {TicketSubCategory} from "../../../../Shared/Types/TicketSubCategory";

import {FindAllTicketCategory} from "../../TicketCategory/TicketCategoryReactQuery";
import {FindByCategoryTicketSubCategory} from "../../TicketSubCategory/TicketSubCategoryReactQuery";
import {FindAllEnabledPowerStationsReactQuery} from "../../PowerStation/PowerStationReactQuery";
import {FindAllSkidsByPowerStationIdReactQuery} from "../../Skid/SkidReactQuery";
import {FindAllEquipmentsBySkidNullOrNotReactQuery} from "../../Equipment/EquipmentReactQuery";

export type TicketFieldsProps = {
    ticket: Ticket,
    isTicketClosed: boolean,
    handleChange: Function
}

const normalizeOptionalId = (rawValue: string): number | null => {
    if (rawValue === "") {
        return null;
    }
    return Number(rawValue);
};

export default function TicketFields({ticket, isTicketClosed, handleChange: handleChangeOwner}: TicketFieldsProps) {
    const [formData, setFormData] = useState<Ticket>(new Ticket());
    const [scheduling, setScheduling] = useState<Dayjs | null>(null);
    const {data: categories} = FindAllTicketCategory();
    const {data: subCategories} = FindByCategoryTicketSubCategory(formData.categoryId);
    const {data: powerStations} = FindAllEnabledPowerStationsReactQuery();
    const {data: skids} = FindAllSkidsByPowerStationIdReactQuery(formData.powerStationId ? formData.powerStationId : undefined);
    const {data: equipments} = FindAllEquipmentsBySkidNullOrNotReactQuery(formData.powerStationId ? formData.powerStationId : undefined, formData.skidId ? formData.skidId : undefined);

    const handleChange = (name: string, value: any) => {
        const data = {
            ...formData,
            [name]: value
        };

        if (name === "categoryId") data.subCategoryId = null;
        if (name === "powerStationId") data.skidId = null;
        if (name === "powerStationId" || name === "skidId") data.equipmentId = null;

        setFormData(data);
        handleChangeOwner(data);
    };

    const handleSchedulingChange = (date: Dayjs | null) => {
        const data = {
            ...formData,
            "scheduling": date ? new Date(date.format()) : null
        };

        setScheduling(date)
        setFormData(data);
        handleChangeOwner(data);
    };

    useEffect(() => {
        const data = {
            ...ticket,
            "status": ticket.status || "OPENED"
        };
        setFormData(data);
        setScheduling(data.scheduling ? dayjs(data.scheduling) : null);
    }, [ticket]);

    return (
        <>
        <ACard>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={4}>
                    <Grid item md={8} xs={12}>
                        <Grid container spacing={4}>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="statusLabel">Status</InputLabel>
                                    <Select
                                        labelId="statusLabel"
                                        name="status"
                                        value={formData.status || "OPENED"}
                                        variant={"standard"}
                                        label="Status"
                                        onChange={(e) => handleChange("status", e.target.value)}
                                        disabled
                                    >
                                        <MenuItem key="OPENED" value="OPENED">Aberto</MenuItem>
                                        <MenuItem key="CLOSED" value="CLOSED">Fechado</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="categoryLabel">Categoria</InputLabel>
                                    <Select
                                        labelId="categoryLabel"
                                        name="categoryId"
                                        value={formData.categoryId ? String(formData.categoryId) : ""}
                                        variant={"standard"}
                                        label="Categoria"
                                        onChange={(e) => handleChange("categoryId", Number(e.target.value))}
                                        error={formData.categoryId === null}
                                        disabled={isTicketClosed}
                                    >
                                        {categories?.data.map((ticketCategory: TicketCategory) => (
                                          <MenuItem key={ticketCategory.id} value={ticketCategory.id}>
                                            {ticketCategory.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {
                                        formData.categoryId === null &&
                                        <FormHelperText error={true}>Campo Obrigatório.</FormHelperText>
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="subCategoryLabel">Sub Categoria</InputLabel>
                                    <Select
                                        labelId="subCategoryLabel"
                                        name="subCategoryId"
                                        value={formData.subCategoryId ? String(formData.subCategoryId) : ""}
                                        variant={"standard"}
                                        label="Sub Categoria"
                                        onChange={(e) => handleChange("subCategoryId", Number(e.target.value))}
                                        error={formData.subCategoryId === null}
                                        disabled={isTicketClosed}
                                    >
                                        {subCategories?.data.map((ticketSubCategory: TicketSubCategory) => (
                                          <MenuItem key={ticketSubCategory.id} value={ticketSubCategory.id}>
                                            {ticketSubCategory.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {
                                        formData.subCategoryId === null &&
                                        <FormHelperText error={true}>Campo Obrigatório.</FormHelperText>
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="powerStationLabel">Usina</InputLabel>
                                    <Select
                                        labelId="powerStationLabel"
                                        name="powerStationId"
                                        value={formData.powerStationId ? String(formData.powerStationId) : ""}
                                        variant={"standard"}
                                        label="Usina"
                                        onChange={(e) => handleChange("powerStationId", Number(e.target.value))}
                                        error={formData.powerStationId === null}
                                        disabled={isTicketClosed}
                                    >
                                        {powerStations?.data?.map((powerStation: { id: number; name: string; }) => (
                                          <MenuItem key={powerStation.id} value={powerStation.id}>
                                            {powerStation.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {
                                        formData.powerStationId === null &&
                                        <FormHelperText error={true}>Campo Obrigatório.</FormHelperText>
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="skidLabel">SKID</InputLabel>
                                    <Select
                                        labelId="skidLabel"
                                        name="skidId"
                                        value={formData.skidId ? String(formData.skidId) : ""}
                                        variant={"standard"}
                                        label="Skid"
                                        onChange={(e) => handleChange("skidId", normalizeOptionalId(e.target.value))}
                                        disabled={isTicketClosed}
                                    >
                                        <MenuItem key="skid-none" value="">
                                            Nenhum
                                        </MenuItem>
                                        {skids?.data?.map((skid: { skidId: number; skidName: string; }) => (
                                          <MenuItem key={skid.skidId} value={skid.skidId}>
                                            {skid.skidName}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="equipmentLabel">Equipamento</InputLabel>
                                    <Select
                                        labelId="equipmentLabel"
                                        name="equipmentId"
                                        value={formData.equipmentId ? String(formData.equipmentId) : ""}
                                        variant={"standard"}
                                        label="Equipamento"
                                        onChange={(e) => handleChange("equipmentId", normalizeOptionalId(e.target.value))}
                                        disabled={isTicketClosed}
                                    >
                                        <MenuItem key="equipment-none" value="">
                                            Nenhum
                                        </MenuItem>
                                        {equipments?.data?.map((equipment: { id: number; name: string; }) => (
                                          <MenuItem key={equipment.id} value={equipment.id}>
                                            {equipment.name}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Agendamento"
                                            inputFormat="DD/MM/YYYY"
                                            value={scheduling}
                                            onChange={handleSchedulingChange}
                                            renderInput={(params) => <TextField name="scheduling" variant={"standard"} error={formData.scheduling === null} {...params} />}
                                            disabled={ticket.id != null}
                                        />
                                    </LocalizationProvider>
                                    {
                                        formData.scheduling === null &&
                                        <FormHelperText error={true}>Campo Obrigatório.</FormHelperText>
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <ToggleButtonGroup
                                        color="primary"
                                        value={formData.type}
                                        exclusive
                                        onChange={(e, value) => handleChange("type", value)}
                                        aria-label="Tipo"
                                        disabled={isTicketClosed}
                                    >
                                        <ToggleButton name="type" value="DEFECT">Defeito</ToggleButton>
                                        <ToggleButton name="type" value="MAINTENANCE">Manutenção</ToggleButton>
                                        <ToggleButton name="type" value="OTHER">Outros</ToggleButton>
                                    </ToggleButtonGroup>
                                    {
                                        formData.type === "" &&
                                        <FormHelperText error={true}>Campo Obrigatório.</FormHelperText>
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        name="subject"
                                        value={formData.subject}
                                        variant="standard"
                                        label="Assunto"
                                        onChange={(e) => handleChange("subject", e.target.value)}
                                        error={formData.subject.trim() === ""}
                                        disabled={isTicketClosed}
                                     />
                                    {
                                        formData.subject.trim() === "" &&
                                        <FormHelperText error={true}>Campo Obrigatório.</FormHelperText>
                                    }
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} marginBottom={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        multiline
                                        rows={9}
                                        name="description"
                                        value={formData.description}
                                        variant="standard"
                                        label="Descrição"
                                        onChange={(e) => handleChange("description", e.target.value)}
                                        error={formData.description.trim() === ""}
                                        disabled={isTicketClosed}
                                     />
                                    {
                                        formData.description.trim() === "" &&
                                        <FormHelperText error={true}>Campo Obrigatório.</FormHelperText>
                                    }
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item md={4} xs={12}>
                        <Grid container>
                            {/* Atendimcnto */}
                            <Grid item xs={12} marginBottom={4}>
                                <TicketMessages ticketId={ticket.id} disabled={!ticket.id || isTicketClosed} />
                            </Grid>

                            {/* Anexos */}
                            <Grid item xs={12}>
                                <TicketAttachments ticketId={ticket.id} disabled={!ticket.id || isTicketClosed} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </ACard>
        </>
    )
}